(() => {
  const CODE = "LOYALTYFREE";
  const VARIANT_ID = 47010430025972;
  const MARKER = "_loyalty_free_gift";
  const ROOT = window.Shopify?.routes?.root || "/";
  let reconciliationPromise;
  let checkoutInProgress = false;

  const cartUrl = (path) =>
    path === "js" ? `${ROOT}cart.js` : `${ROOT}cart/${path}`;

  async function request(path, options = {}) {
    const response = await fetch(cartUrl(path), {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      ...options,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || data.message || "The cart could not be updated.");
    }
    return data;
  }

  function isMarkedGift(item) {
    return (
      Number(item.variant_id) === VARIANT_ID &&
      item.properties?.[MARKER] === CODE
    );
  }

  function hasQualifyingItem(cart) {
    return cart.items.some(
      (item) => Number(item.variant_id) !== VARIANT_ID && item.quantity > 0
    );
  }

  function codeDiscounts(cart) {
    const codes = new Set();
    const applications = [
      ...(cart.cart_level_discount_applications || []),
      ...cart.items.flatMap((item) =>
        (item.line_level_discount_allocations || []).map(
          (allocation) => allocation.discount_application
        )
      ),
    ];

    applications.forEach((application) => {
      if (
        application?.type === "discount_code" &&
        application.title &&
        application.title.toUpperCase() !== CODE
      ) {
        codes.add(application.title);
      }
    });
    return [...codes];
  }

  function loyaltyDiscountApplied(cart) {
    return cart.items.some(
      (item) =>
        isMarkedGift(item) &&
        (item.line_level_discount_allocations || []).some(
          (allocation) =>
            allocation.discount_application?.title?.toUpperCase() === CODE
        )
    );
  }

  async function removeMarkedGifts(cart) {
    const gifts = cart.items.filter(isMarkedGift);
    for (const gift of gifts) {
      await request("change.js", {
        method: "POST",
        body: JSON.stringify({ id: gift.key, quantity: 0 }),
      });
    }
  }

  async function updateDiscounts(codes) {
    return request("update.js", {
      method: "POST",
      body: JSON.stringify({ discount: codes.join(",") }),
    });
  }

  async function refreshCartUI() {
    const sectionIds = [];
    if (document.getElementById("minicart-form")) sectionIds.push("minicart-form");
    if (document.getElementById("main-cart-items")) sectionIds.push("main-cart-items");

    if (!sectionIds.length) {
      window.location.reload();
      return;
    }

    const response = await fetch(
      `${ROOT}cart?sections=${encodeURIComponent(sectionIds.join(","))}`,
      { credentials: "same-origin" }
    );
    const sections = await response.json();

    sectionIds.forEach((id) => {
      if (!sections[id]) return;
      const parsed = new DOMParser().parseFromString(sections[id], "text/html");
      const replacement = parsed.getElementById(id);
      const current = document.getElementById(id);
      if (replacement && current) current.replaceWith(replacement);
    });

    const cart = await request("js");
    document.querySelectorAll(".cart-count").forEach((element) => {
      element.textContent = element.classList.contains("cart-count-drawer")
        ? `(${cart.item_count})`
        : cart.item_count > 100
          ? "~"
          : cart.item_count;
    });
    document.querySelector("header-total-price")?.updateTotal?.(cart);
    document.querySelector("cart-notification")?.cartAction?.();
    window.BlsLazyloadImg?.init?.();
  }

  async function reconcileGift() {
    if (reconciliationPromise) return reconciliationPromise;
    reconciliationPromise = (async () => {
      const cart = await request("js");
      const gifts = cart.items.filter(isMarkedGift);
      if (!gifts.length) return false;

      if (!hasQualifyingItem(cart)) {
        const otherCodes = codeDiscounts(cart);
        await removeMarkedGifts(cart);
        await updateDiscounts(otherCodes);
        return true;
      }

      if (gifts.length > 1 || gifts.some((gift) => gift.quantity !== 1)) {
        await removeMarkedGifts(cart);
        await request("add.js", {
          method: "POST",
          body: JSON.stringify({
            items: [
              {
                id: VARIANT_ID,
                quantity: 1,
                properties: { [MARKER]: CODE },
              },
            ],
          }),
        });
        return true;
      }
      return false;
    })().finally(() => {
      reconciliationPromise = undefined;
    });
    return reconciliationPromise;
  }

  class LoyaltyFreeGift extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";
      this.form = this.querySelector("[data-loyalty-form]");
      this.input = this.querySelector('input[name="loyalty_code"]');
      this.message = this.querySelector("[data-loyalty-message]");
      this.form?.addEventListener("submit", (event) => this.apply(event));
      this.querySelector("[data-loyalty-remove]")?.addEventListener(
        "click",
        () => this.remove()
      );
    }

    setMessage(message, state) {
      if (!this.message) return;
      this.message.textContent = message;
      this.message.classList.toggle("is-error", state === "error");
      this.message.classList.toggle("is-success", state === "success");
    }

    setLoading(loading) {
      this.classList.toggle("is-loading", loading);
      this.setAttribute("aria-busy", String(loading));
    }

    async apply(event) {
      event.preventDefault();
      const enteredCode = this.input?.value.trim().toUpperCase();
      if (enteredCode !== CODE) {
        this.setMessage("That loyalty code is not valid.", "error");
        return;
      }

      this.setLoading(true);
      try {
        let cart = await request("js");
        if (!hasQualifyingItem(cart)) {
          this.setMessage(
            "Add at least one other product before using this code.",
            "error"
          );
          return;
        }

        const previousCodes = codeDiscounts(cart);
        if (!cart.items.some(isMarkedGift)) {
          await request("add.js", {
            method: "POST",
            body: JSON.stringify({
              items: [
                {
                  id: VARIANT_ID,
                  quantity: 1,
                  properties: { [MARKER]: CODE },
                },
              ],
            }),
          });
        }

        cart = await updateDiscounts([...previousCodes, CODE]);
        if (!loyaltyDiscountApplied(cart)) {
          await removeMarkedGifts(cart);
          await updateDiscounts(previousCodes);
          this.setMessage(
            "This code could not be applied. It may already have been redeemed.",
            "error"
          );
          return;
        }

        this.setMessage("Your Loyalty Free Gift has been added.", "success");
        await refreshCartUI();
      } catch (error) {
        this.setMessage(
          error.message || "The loyalty reward could not be applied. Please try again.",
          "error"
        );
      } finally {
        this.setLoading(false);
      }
    }

    async remove() {
      this.setLoading(true);
      try {
        const cart = await request("js");
        const otherCodes = codeDiscounts(cart);
        await removeMarkedGifts(cart);
        await updateDiscounts(otherCodes);
        await refreshCartUI();
      } catch (error) {
        this.setMessage(
          error.message || "The loyalty reward could not be removed.",
          "error"
        );
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get("loyalty-free-gift")) {
    customElements.define("loyalty-free-gift", LoyaltyFreeGift);
  }

  document.addEventListener(
    "click",
    async (event) => {
      const checkout = event.target.closest('[name="checkout"]');
      if (!checkout || checkoutInProgress) return;

      event.preventDefault();
      checkoutInProgress = true;
      try {
        const changed = await reconcileGift();
        if (changed) await refreshCartUI();
        const checkoutForm = checkout.getAttribute("form");
        const currentCheckout =
          (checkout.id ? document.getElementById(checkout.id) : null) ||
          (checkoutForm
            ? document.querySelector(
                `[name="checkout"][form="${CSS.escape(checkoutForm)}"]`
              )
            : null) ||
          document.querySelector('[name="checkout"]');
        currentCheckout?.click();
        checkoutInProgress = false;
      } catch (error) {
        checkoutInProgress = false;
        console.error("Loyalty gift validation failed:", error);
        window.location.assign(`${ROOT}cart`);
      }
    },
    true
  );
})();
